/**
 * useEmployeeForm — formData, loading, submit, upload handlers, trainings, qualifications, documents, certifications.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { employeesAPI, uploadAPI, locationsAPI, branchesAPI, permissionsAPI, companyAPI } from '../../lib/api';
import { emptyEmployee } from './helpers';

// Fields that the PATCH endpoint supports. Computed diff is restricted to these
// to avoid sending frontend-only / computed fields like `location_name`, `branch_name`, etc.
const PATCHABLE_FIELDS = [
  'employee_number', 'first_name', 'last_name', 'email', 'phone', 'mobile',
  'date_of_birth', 'place_of_birth', 'gender', 'nationality',
  'street', 'house_number', 'postal_code', 'city', 'country',
  'position', 'position_list', 'department', 'location_id', 'additional_location_ids', 'org_unit_id',
  'taetigkeitsart',
  'employment_start', 'employment_end', 'is_temporary', 'employment_type',
  'working_time_model', 'weekly_hours', 'vacation_days_per_year', 'vacation_carryover',
  'business_phone', 'business_mobile', 'business_email',
  'iban', 'bic', 'bank_name',
  'qualifications', 'certifications',
  'emergency_contact', 'police_check', 'measles_protection',
  'trainings', 'custom_fields',
  'notes', 'is_active', 'pin', 'documents', 'contracts',
  'reminder_enabled', 'reminder_threshold_minutes', 'pre_shift_reminder_minutes', 'end_shift_reminder_minutes',
  'overtime_starting_balance', 'overtime_account_start',
];

const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// Normalize a formData-shaped object into the backend payload shape.
// Applied to BOTH the original snapshot and the new formData before diffing,
// so nested objects like emergency_contact (which is set to null when .name is empty)
// compare equally and don't show up as spurious changes.
const normalizeForBackend = (data) => ({
  ...data,
  weekly_hours: parseFloat(data.weekly_hours) || 39,
  vacation_days_per_year: parseInt(data.vacation_days_per_year) || 30,
  vacation_carryover: parseInt(data.vacation_carryover) || 0,
  emergency_contact: data.emergency_contact?.name ? data.emergency_contact : null,
  police_check: data.police_check?.issue_date ? data.police_check : null,
  measles_protection: data.measles_protection?.proof_date ? data.measles_protection : null,
  trainings: data.trainings || [],
  qualifications: data.qualifications || [],
  certifications: data.certifications || [],
  documents: data.documents || [],
  contracts: data.contracts || [],
});

export function useEmployeeForm({ employeeId, isEdit, linkedUser, userPermissions }) {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(emptyEmployee);
  const [locations, setLocations] = useState([]);
  const [branches, setBranches] = useState([]);
  const [orgTree, setOrgTree] = useState([]);
  const [orgLevels, setOrgLevels] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingQualification, setUploadingQualification] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);

  // Snapshot of the originally loaded employee — used to compute the PATCH diff on submit
  const originalDataRef = useRef(null);

  const [newTraining, setNewTraining] = useState({
    title: '', provider: '', date: '', expiry_date: '',
    certificate_url: '', certificate_filename: '', description: '', hours: '',
  });
  const [newQualification, setNewQualification] = useState({
    name: '', date: '', expiry_date: '', document_url: '', document_filename: '', editing_id: null,
  });
  const [newCertification, setNewCertification] = useState('');
  const [newDocument, setNewDocument] = useState({ title: '', url: '', filename: '' });
  const [newContract, setNewContract] = useState({
    contract_type: 'arbeitsvertrag', title: '', valid_from: '', valid_until: '',
    document_url: '', document_filename: '', notes: '', reminder_value: '', reminder_unit: 'days',
  });

  const loadBaseData = useCallback(async () => {
    try {
      const [locRes, branchRes, treeRes] = await Promise.all([
        locationsAPI.getAll(),
        branchesAPI.getAll(),
        companyAPI.getOrgTree().catch(() => ({ data: { tree: [], levels: [] } })),
      ]);
      setLocations(locRes.data);
      setBranches(branchRes.data);
      setOrgTree(treeRes.data?.tree || []);
      setOrgLevels(treeRes.data?.levels || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  }, []);

  const loadEmployee = useCallback(async () => {
    try {
      const response = await employeesAPI.getOne(employeeId);
      const merged = {
        ...emptyEmployee,
        ...response.data,
        additional_location_ids: response.data.additional_location_ids || [],
        emergency_contact: response.data.emergency_contact || emptyEmployee.emergency_contact,
        police_check: response.data.police_check || emptyEmployee.police_check,
        measles_protection: response.data.measles_protection || emptyEmployee.measles_protection,
        trainings: response.data.trainings || [],
        documents: response.data.documents || [],
        contracts: response.data.contracts || [],
      };
      setFormData(merged);
      // Deep-clone snapshot for diff computation on submit
      originalDataRef.current = JSON.parse(JSON.stringify(merged));
    } catch (error) {
      toast.error('Mitarbeiter nicht gefunden');
      navigate('/employees');
    } finally {
      setLoading(false);
    }
  }, [employeeId, navigate]);

  useEffect(() => {
    loadBaseData();
    if (isEdit) loadEmployee();
  }, [employeeId, isEdit, loadBaseData, loadEmployee]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));
  const handleEmergencyContactChange = (field, value) => setFormData((prev) => ({ ...prev, emergency_contact: { ...prev.emergency_contact, [field]: value } }));
  const handlePoliceCheckChange = (field, value) => setFormData((prev) => ({ ...prev, police_check: { ...prev.police_check, [field]: value } }));
  const handleMeaslesChange = (field, value) => setFormData((prev) => ({ ...prev, measles_protection: { ...prev.measles_protection, [field]: value } }));

  const isMeaslesRequired = () => {
    if (!formData.date_of_birth) return false;
    const year = parseInt(formData.date_of_birth.substring(0, 4));
    return year > 1970;
  };

  // ---- Uploads ----
  const handleCertificateUpload = async (e, forTraining = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const empName = `${formData.last_name || ''}_${formData.first_name || ''}`.trim();
      const docTitle = forTraining ? (newTraining.title || '') : '';
      const docDate = forTraining ? (newTraining.date || '') : '';
      const response = await uploadAPI.uploadCertificate(file, {
        employee_name: empName, document_title: docTitle, document_date: docDate,
      });
      if (forTraining) {
        setNewTraining((prev) => ({ ...prev, certificate_url: response.data.url, certificate_filename: response.data.original_filename }));
      }
      toast.success('Datei hochgeladen');
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleQualificationFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur PDF, JPG und PNG Dateien erlaubt');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei ist zu groß (max. 10MB)');
      return;
    }
    setUploadingQualification(true);
    try {
      const empName = `${formData.last_name || ''}_${formData.first_name || ''}`.trim();
      const response = await uploadAPI.uploadCertificate(file, {
        employee_name: empName, document_title: newQualification.name || '',
      });
      setNewQualification((prev) => ({ ...prev, document_url: response.data.url, document_filename: response.data.original_filename }));
      toast.success('Dokument hochgeladen');
    } catch {
      toast.error('Fehler beim Hochladen');
    } finally {
      setUploadingQualification(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDocument(true);
    try {
      const empName = `${formData.last_name || ''}_${formData.first_name || ''}`.trim();
      const today = new Date().toISOString().slice(0, 10);
      const response = await uploadAPI.uploadCertificate(file, {
        employee_name: empName, document_title: newDocument.title || '', document_date: today,
      });
      setNewDocument((prev) => ({ ...prev, url: response.data.url, filename: response.data.original_filename }));
      toast.success('Datei hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingDocument(false);
    }
  };

  // ---- Trainings ----
  const addTraining = () => {
    if (!newTraining.title || !newTraining.date) {
      toast.error('Titel und Datum sind erforderlich');
      return;
    }
    const training = {
      id: `temp-${Date.now()}`,
      ...newTraining,
      hours: newTraining.hours ? parseFloat(newTraining.hours) : null,
      created_at: new Date().toISOString(),
    };
    setFormData((prev) => ({ ...prev, trainings: [...prev.trainings, training] }));
    setNewTraining({ title: '', provider: '', date: '', expiry_date: '', certificate_url: '', certificate_filename: '', description: '', hours: '' });
  };
  const removeTraining = (trainingId) => setFormData((prev) => ({ ...prev, trainings: prev.trainings.filter((t) => t.id !== trainingId) }));

  // ---- Qualifications ----
  const addQualification = () => {
    if (newQualification.name.trim()) {
      const qualification = {
        id: newQualification.editing_id || `qual-${Date.now()}`,
        name: newQualification.name.trim(),
        date: newQualification.date || '',
        expiry_date: newQualification.expiry_date || '',
        document_url: newQualification.document_url || '',
        document_filename: newQualification.document_filename || '',
        added_at: new Date().toISOString(),
      };
      if (newQualification.editing_id) {
        setFormData((prev) => ({
          ...prev,
          qualifications: (prev.qualifications || []).map((q) =>
            typeof q === 'object' && q.id === newQualification.editing_id ? qualification : q
          ),
        }));
      } else {
        setFormData((prev) => ({ ...prev, qualifications: [...(prev.qualifications || []), qualification] }));
      }
      setNewQualification({ name: '', date: '', expiry_date: '', document_url: '', document_filename: '', editing_id: null });
    }
  };
  const removeQualification = (qualId) => setFormData((prev) => ({
    ...prev,
    qualifications: (prev.qualifications || []).filter((q) => (typeof q === 'object' ? q.id !== qualId : false)),
  }));

  // ---- Certifications ----
  const addCertification = () => {
    if (newCertification.trim()) {
      setFormData((prev) => ({ ...prev, certifications: [...prev.certifications, newCertification.trim()] }));
      setNewCertification('');
    }
  };
  const removeCertification = (index) => setFormData((prev) => ({ ...prev, certifications: prev.certifications.filter((_, i) => i !== index) }));

  // ---- Documents ----
  const addDocument = () => {
    if (!newDocument.url) {
      toast.error('Bitte zuerst eine Datei hochladen');
      return;
    }
    const doc = {
      id: `doc-${Date.now()}`,
      title: newDocument.title || '',
      filename: newDocument.filename,
      url: newDocument.url,
      uploaded_at: new Date().toISOString(),
    };
    setFormData((prev) => ({ ...prev, documents: [...(prev.documents || []), doc] }));
    setNewDocument({ title: '', url: '', filename: '' });
    toast.success('Dokument hinzugefügt');
  };
  const removeDocument = (docId) => setFormData((prev) => ({ ...prev, documents: (prev.documents || []).filter((d) => d.id !== docId) }));

  // ---- Contracts (Arbeitsverträge & Ergänzungen) ----
  const handleContractFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Nur PDF, JPG und PNG Dateien erlaubt');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Datei ist zu groß (max. 10MB)');
      return;
    }
    setUploadingContract(true);
    try {
      const empName = `${formData.last_name || ''}_${formData.first_name || ''}`.trim();
      const response = await uploadAPI.uploadCertificate(file, {
        employee_name: empName, document_title: newContract.title || '', document_date: newContract.valid_from || '',
      });
      setNewContract((prev) => ({ ...prev, document_url: response.data.url, document_filename: response.data.original_filename }));
      toast.success('Dokument hochgeladen');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hochladen');
    } finally {
      setUploadingContract(false);
    }
  };

  const addContract = () => {
    if (!newContract.title.trim()) {
      toast.error('Bitte eine Bezeichnung eingeben');
      return;
    }
    const contract = {
      id: `contract-${Date.now()}`,
      contract_type: newContract.contract_type || 'arbeitsvertrag',
      title: newContract.title.trim(),
      valid_from: newContract.valid_from || '',
      valid_until: newContract.valid_until || '',
      document_url: newContract.document_url || '',
      document_filename: newContract.document_filename || '',
      notes: newContract.notes || '',
      reminder_value: (newContract.reminder_value === '' || newContract.reminder_value == null)
        ? null : parseInt(newContract.reminder_value, 10),
      reminder_unit: (newContract.reminder_value === '' || newContract.reminder_value == null)
        ? null : (newContract.reminder_unit || 'days'),
      created_at: new Date().toISOString(),
    };
    setFormData((prev) => ({ ...prev, contracts: [...(prev.contracts || []), contract] }));
    setNewContract({ contract_type: 'arbeitsvertrag', title: '', valid_from: '', valid_until: '', document_url: '', document_filename: '', notes: '', reminder_value: '', reminder_unit: 'days' });
    toast.success('Vertrag hinzugefügt');
  };
  const removeContract = (contractId) => setFormData((prev) => ({ ...prev, contracts: (prev.contracts || []).filter((c) => c.id !== contractId) }));

  // ---- Submit ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name) {
      toast.error('Bitte Vorname und Nachname ausfüllen');
      return;
    }
    setSaving(true);
    try {
      // Normalized full payload (used for CREATE, and as the "new" side for PATCH diff)
      const normalized = normalizeForBackend(formData);

      if (isEdit) {
        // Compute PATCH diff: only fields that actually changed vs. the loaded snapshot
        // Normalize the snapshot too so null-coalescing of nested objects doesn't create false diffs
        const originalNormalized = originalDataRef.current
          ? normalizeForBackend(originalDataRef.current)
          : {};
        const patchBody = {};
        for (const field of PATCHABLE_FIELDS) {
          const nextValue = normalized[field];
          const prevValue = originalNormalized[field];
          if (!isEqual(nextValue, prevValue)) {
            patchBody[field] = nextValue;
          }
        }

        // If nothing changed on the employee itself, skip the API call entirely.
        // Permission updates still run below if needed.
        if (Object.keys(patchBody).length > 0) {
          await employeesAPI.patch(employeeId, patchBody);
          // Deep-clone so nested objects (emergency_contact etc.) are properly isolated
          // from subsequent user edits in the same session.
          originalDataRef.current = JSON.parse(JSON.stringify(formData));
        }

        if (linkedUser && userPermissions?.role_id) {
          try {
            await permissionsAPI.update(linkedUser.id, {
              user_id: linkedUser.id,
              role_id: userPermissions.role_id,
              scope_type: userPermissions.scope_type,
              branch_ids: userPermissions.branch_ids,
              location_ids: userPermissions.location_ids,
              all_branches: userPermissions.scope_type === 'all',
              all_locations: userPermissions.scope_type === 'all',
            });
          } catch (permError) {
            console.error('Permission save failed:', permError);
          }
        }
        toast.success('Mitarbeiter aktualisiert');
      } else {
        await employeesAPI.create(normalized);
        toast.success('Mitarbeiter erstellt');
      }
      navigate('/employees');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map((d) => d.msg || JSON.stringify(d)).join(', '));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Fehler beim Speichern');
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    formData, setFormData,
    locations, branches,
    orgTree, orgLevels,
    loading, saving,
    uploading, uploadingQualification, uploadingDocument, uploadingContract,
    newTraining, setNewTraining,
    newQualification, setNewQualification,
    newCertification, setNewCertification,
    newDocument, setNewDocument,
    newContract, setNewContract,
    handleChange, handleEmergencyContactChange, handlePoliceCheckChange, handleMeaslesChange,
    isMeaslesRequired,
    handleCertificateUpload, handleQualificationFileUpload, handleDocumentUpload, handleContractFileUpload,
    addTraining, removeTraining,
    addQualification, removeQualification,
    addCertification, removeCertification,
    addDocument, removeDocument,
    addContract, removeContract,
    handleSubmit,
  };
}
